import React, { useEffect, useRef, useState } from 'react';

const ShowcaseSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Added mobile span rules (col-span-X row-span-Y) before the desktop (md:) rules
  const showcaseItems = [
    {
      id: 1,
      type: 'image',
      style: 'full',
      url: '/Images/experienced-cues-439231.framer.app_ (4).png',
      title: 'Portfolite SS',
      span: 'col-span-2 row-span-2 md:col-span-2 md:row-span-2'
    },
    {
      id: 2,
      type: 'video',
      style: 'full',
      url: 'https://res.cloudinary.com/dy6qwpxkr/video/upload/w_800,q_auto:good,f_auto/v1772808795/2026-03-06_14-50-30_qpf341.mp4',
      title: 'Web Application Demo',
      span: 'col-span-2 row-span-1 md:col-span-2 md:row-span-1'
    },
    {
      id: 3,
      type: 'video',
      style: 'full',
      url: 'https://res.cloudinary.com/dy6qwpxkr/video/upload/q_auto:best,f_auto/v1772812013/2026-03-06_15-22-54_s7gx2j.mp4',
      title: 'Dashboard Design',
      span: 'col-span-2 row-span-2 md:col-span-2 md:row-span-3'
    },
    {
      id: 4,
      type: 'image',
      style: 'full',
      url: '/Images/minarch.png',
      title: 'Analytics Platform',
      span: 'col-span-1 row-span-1 md:col-span-2 md:row-span-1' // Side-by-side square on mobile
    },
    {
      id: 5,
      type: 'image',
      style: 'full',
      url: '/Images/loginpage.png',
      title: 'SaaS Interface',
      span: 'col-span-1 row-span-1 md:col-span-2 md:row-span-1' // Side-by-side square on mobile
    },
    {
      id: 6,
      type: 'video',
      style: 'full',
      url: 'https://res.cloudinary.com/dy6qwpxkr/video/upload/w_800,q_auto:good,f_auto/v1772552953/Snippetfinal_kyl5qw.mp4',
      title: 'E-Commerce Site',
      span: 'col-span-2 row-span-1 md:col-span-2 md:row-span-1'
    },
    {
      id: 7,
      type: 'video',
      style: 'full',
      url: 'https://res.cloudinary.com/dy6qwpxkr/video/upload/w_800,q_auto:good,f_auto/v1772705338/HighResSnip_cztgfg.mp4',
      title: 'Web Application',
      span: 'col-span-2 row-span-1 md:col-span-2 md:row-span-1'
    },
    {
      id: 9,
      type: 'image',
      style: 'full',
      url: '/Images/mobilesite.png',
      title: 'Portfolio Site',
      span: 'col-span-1 row-span-2 md:col-span-1 md:row-span-2'
    },
    {
      id: 10,
      type: 'image',
      style: 'full',
      url: '/Images/frankocean.png',
      title: 'Landing Page',
      span: 'col-span-1 row-span-2 md:col-span-2 md:row-span-1'
    },
    {
      id: 11,
      type: 'image',
      style: 'full',
      url: '/Images/voyager.png',
      title: 'Landing Page',
      span: 'col-span-2 row-span-1 md:col-span-1 md:row-span-1'
    },
    {
      id: 12,
      type: 'image',
      style: 'full',
      url: '/Images/creativeforce.png',
      title: 'Landing Page',
      span: 'col-span-2 row-span-2 md:col-span-2 md:row-span-2'
    },
    {
      id: 14,
      type: 'image',
      style: 'full',
      url: '/Images/vanguard.png',
      title: 'Landing Page',
      span: 'col-span-2 row-span-2 md:col-span-3 md:row-span-2',
      position: 'object-left'
    },
    {
      id: 15,
      type: 'image',
      style: 'full',
      url: '/Images/dashboard.png',
      title: 'Landing Page',
      span: 'col-span-2 row-span-1 md:col-span-1 md:row-span-1'
    },
  ];

  return (
    <section id="work" ref={sectionRef} className="relative py-24 lg:py-32 bg-zinc-950 overflow-hidden">

      <div className={`max-w-7xl mx-auto px-6 lg:px-8 relative z-10 text-center mb-16 transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
          Whats <span className="text-[#4a8282]">Been Built</span>
        </h2>
        <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
          A collection of modern, responsive designs across various industries and use cases.
        </p>
      </div>

      <div className="w-full px-3 md:px-4 mx-auto">
        {/* Adjusted mobile row height to 180px so phone boxes look crisp and proportionate */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 auto-rows-[180px] md:auto-rows-[300px]">
          {showcaseItems.map((item, index) => (
            <div
              key={item.id}
              className={`${item.span} relative overflow-hidden rounded-[24px] transition-all duration-700 group ${item.style === 'padded' ? 'bg-zinc-900 flex items-center justify-center' : 'bg-black'
                } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
              style={{ transitionDelay: `${(index % 6) * 100}ms` }}
            >
              {item.type === 'video' ? (
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  className={`transition-transform duration-[2000ms] ease-out group-hover:scale-105 ${item.style === 'padded'
                    ? 'w-[85%] h-[85%] object-contain rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] bg-white'
                    : 'absolute inset-0 w-full h-full object-cover'
                    }`}
                >
                  {isVisible && <source src={item.url} type="video/mp4" />}
                </video>
              ) : (
                <img
                  src={item.url}
                  alt={item.title}
                  loading="lazy"
                  className={`transition-transform duration-[2000ms] ease-out group-hover:scale-105 ${item.style === 'padded'
                    ? 'w-[85%] h-[85%] object-contain rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] bg-white'
                    : `absolute inset-0 w-full h-full object-cover ${item.position || 'object-center'}`
                    }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="absolute bottom-0 left-0 w-full h-64 bg-gradient-to-b from-transparent to-white pointer-events-none"></div>
    </section>
  );
};

export default ShowcaseSection;